import { useParams, Link } from 'react-router';
import { useGetHuntDetail } from '@cdf/api-client-react';
import { QRCodeCanvas } from 'qrcode.react';
import { PrinterIcon, ArrowLeftIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function HuntPosterPage() {
  const { huntId, stationId } = useParams<{ huntId: string; stationId?: string }>();
  const { data: hunt, isLoading, isError } = useGetHuntDetail(huntId ?? '');

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-muted-foreground">Loading hunt…</p>
      </div>
    );
  }

  if (isError || !hunt) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">Hunt not found or you don't have access.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/workspace/hunts">Back to Hunts</Link>
          </Button>
        </div>
      </div>
    );
  }

  const stations = stationId
    ? hunt.stations.filter((s) => s.id === stationId)
    : [...hunt.stations].sort((a, b) => a.sortOrder - b.sortOrder);

  if (stations.length === 0) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">No stations to print posters for.</p>
          <Button asChild variant="outline" size="sm">
            <Link to={`/workspace/hunts/${hunt.id}`}>Back to Hunt</Link>
          </Button>
        </div>
      </div>
    );
  }

  const checkinUrl = (code: string) =>
    `${window.location.origin}/hunt/${hunt.slug}/checkin/${code}`;

  return (
    <div className="min-h-dvh bg-white">
      <style>{`
        @media screen {
          .poster-page {
            max-width: 800px;
            margin: 0 auto 24px;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
          }
        }
        @media print {
          .no-print { display: none !important; }
          .poster-page {
            page-break-after: always;
            break-after: page;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            max-width: none !important;
          }
          .poster-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          body { margin: 0; }
        }
      `}</style>

      {/* Toolbar (hidden when printing) */}
      <div className="no-print sticky top-0 z-10 border-b bg-slate-50 px-4 py-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/workspace/hunts/${hunt.id}`}>
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Hunt
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {stations.length} {stations.length === 1 ? 'poster' : 'posters'}
            </span>
            <Button size="sm" onClick={() => window.print()}>
              <PrinterIcon className="mr-2 h-4 w-4" />
              Print {stations.length === 1 ? 'Poster' : 'All'}
            </Button>
          </div>
        </div>
      </div>

      {/* Posters */}
      <div className="py-6">
        {stations.map((station) => (
          <div
            key={station.id}
            className="poster-page flex flex-col items-center justify-center px-8 py-12"
            style={{ minHeight: '100vh' }}
          >
            {/* Hunt name */}
            <div className="mb-2 text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-slate-400">
                Treasure Hunt
              </p>
              <h1 className="text-2xl font-bold text-slate-900">{hunt.name}</h1>
            </div>

            {/* Station name + points */}
            <div className="mb-6 text-center">
              <h2 className="text-3xl font-extrabold text-slate-900">{station.name}</h2>
              <span className="mt-2 inline-block rounded-full bg-amber-100 px-4 py-1 text-sm font-semibold text-amber-700">
                {station.points} points
              </span>
            </div>

            {/* QR code */}
            <div className="rounded-2xl border-2 border-slate-200 bg-white p-6 shadow-lg">
              <QRCodeCanvas
                value={checkinUrl(station.secretCode)}
                size={320}
                level="M"
                marginSize={1}
              />
            </div>

            {/* Instructions */}
            <div className="mt-6 max-w-md text-center">
              <p className="text-lg font-medium text-slate-700">
                Scan this QR code with your phone camera to check in
              </p>
              <p className="mt-1 text-sm text-slate-400">
                Join the hunt with a nickname, then capture this station
              </p>
            </div>

            {/* Station code (small, for admin reference) */}
            <p className="mt-8 font-mono text-xs text-slate-300">
              Code: {station.secretCode}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
